import React from "react";

export interface TabOption {
  label: string | React.ReactNode;
  value: string;
}

interface GenericTabsProps {
  tabs: TabOption[];
  activeTab: string;
  onTabChange: (tabValue: string) => void;
  className?: string;
  /** Extra-compact segmented control (minimal padding, text-xs). */
  size?: "xs" | "sm" | "md" | "lg";
  /** When false, tabs only span content width (segmented pill). Default true. */
  fullWidth?: boolean;
  /**
   * When true, the bar sizes to its tabs (w-max) with no horizontal scroll; use a parent
   * min-width (e.g. min-w-[50%]) if you want a floor width. Default false keeps full width + overflow-x-auto.
   */
  fitContent?: boolean;
  /** Accessible label for the tab group. */
  "aria-label"?: string;
}

const GenericTabs: React.FC<GenericTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  size = "md",
  fullWidth = true,
  fitContent = false,
  "aria-label": ariaLabel,
}) => {
  // Size-based styling
  const sizeStyles = {
    xs: {
      container: "p-0.5 rounded-md",
      button:
        "px-3 py-0.5 text-xs font-medium rounded-[6px] min-h-[28px] flex-none",
    },
    sm: {
      container: "p-0.5 rounded-sm",
      button: "px-6 py-1 text-body-small font-medium",
    },
    md: {
      container: "p-1 rounded-sm",
      button: "px-8 py-2 text-sm font-medium",
    },
    lg: {
      container: "p-1.5 rounded-sm",
      button: "px-10 py-2.5 text-base font-medium",
    },
  };

  const currentSize = sizeStyles[size];

  const activeTabClasses =
    size === "xs"
      ? "bg-semantic-bg-primary shadow-sm text-semantic-text-primary"
      : "bg-semantic-bg-primary-alt shadow text-semantic-text-primary";

  return (
    <div
      className={`
        border
        bg-semantic-bg-secondary-subtle
        border-semantic-border-primary
        ${fitContent
          ? "w-full max-w-full min-w-0 overflow-x-visible lg:w-max"
          : "w-full min-w-0 overflow-x-auto"}
        ${currentSize.container}
        ${className || ""}
      `}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`
          flex
          ${fullWidth
            ? fitContent
              ? ""
              : "min-w-full w-max"
            : "w-fit max-w-full"}
        `}
      >
        {tabs.map((tab) => (
          <button
            key={tab.value}
            role="tab"
            aria-selected={activeTab === tab.value}
            aria-controls={`tabpanel-${tab.value.replaceAll(" ", "-")}`}
            id={`tab-${tab.value.replaceAll(" ", "-")}`}
            data-testid={`tab-${tab.value.replaceAll(" ", "-")}`}
            className={`
            flex
            ${fullWidth ? "grow shrink-0 basis-0" : "flex-none shrink-0"}
            ${currentSize.button}
            rounded-sm transition-colors duration-150
            whitespace-nowrap
            justify-center
            items-center
            ${activeTab === tab.value
              ? activeTabClasses
              : "bg-transparent text-semantic-text-secondary hover:text-gray-700"}
          `}
            onClick={() => onTabChange(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default GenericTabs; 